import { useGetUser, type CompanyType } from '@sit/client-shared';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { useGetEmployees } from '@web/api/employees/use-get-employees';
import Button from '@web/components/Shared/Button';
import Dialog from '@web/components/Shared/Dialog';
import Box from 'carbon-react/lib/components/box';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import DateInput, { type DateChangeEvent } from 'carbon-react/lib/components/date';
import Form from 'carbon-react/lib/components/form';
import { FilterableSelect, Option, type CustomSelectChangeEvent } from 'carbon-react/lib/components/select';
import Textarea from 'carbon-react/lib/components/textarea';
import I18njs from 'i18n-js';
import moment, { type Moment } from 'moment';
import { useState } from 'react';
import styled from 'styled-components';
import Translate from '../../../components/Translate/Translate';
import { TimesheetStartDayValidator } from '../../../helpers/validations';
import { getTimesheetEndDate } from '../helpers/timesheets';
import { FieldHelp } from './FieldHelp';

const InputContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]};
`;

const DateInputContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[2]};
`;

const SelectContent = styled.div`
  width: 344px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[1]};
`;
const SelectLabel = styled(Translate)`
  font-weight: 600;
`;

const DescriptionField = styled.div`
  width: 344px;
`;

const FieldBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]};
  align-items: center;
`;

const TIME_FORMAT = 'YYYY-MM-DD';

export interface FormData {
  startDate: Moment;
  endDate: Moment;
  description: string;
  employeeId?: string;
  includeEntries?: boolean;
}

interface CreateTimesheetModalProps {
  title?: string;
  data: FormData;
  showIncludeEntriesBox?: boolean;
  submitButtonText?: string;
  staff?: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
}

const validateStartDate = (company: CompanyType, startDate: Moment) => {
  const validation = new TimesheetStartDayValidator(company);
  if (!validation.validate(startDate.format(TIME_FORMAT))) {
    return validation.message();
  }
  return null;
};

export const CreateTimesheetModal = ({
  title = 'timesheets.CreateTimesheet',
  data,
  showIncludeEntriesBox = false,
  onClose,
  onSubmit,
  submitButtonText = 'timesheets.CreateTimesheet',
  staff,
}: CreateTimesheetModalProps) => {
  const [formData, setFormData] = useState<FormData>(data);
  const { startDate, endDate, description, includeEntries, employeeId } = formData;
  const { data: emloyeesData } = useGetEmployees();
  const { data: userData } = useGetUser();
  const { data: company } = useGetCurrentCompany();

  if (!company) {
    return null;
  }

  const updateFieldValue = (field: keyof FormData, value: FormData[typeof field]) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const onStartDateFieldChange = (ev: DateChangeEvent) => {
    const { rawValue } = ev.target.value;

    const value = moment.utc(rawValue, TIME_FORMAT);

    updateFieldValue('startDate', value);

    const newEndDate = getTimesheetEndDate(company, value);

    updateFieldValue('endDate', newEndDate);
  };

  const updateTextField =
    (field: keyof FormData) =>
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      updateFieldValue(field, target.value);
    };

  const handleEmployeeChange = (event: CustomSelectChangeEvent) => {
    // `event.selectionConfirmed` is a custom property in the `FilterableSelect` component's `onChange` callback
    // to indicate that the user has selected an option.
    // We don't want to call the component callback's `onChange` if the user has not selected an option.
    // Without this check, every time the user types a character, the we will call `updateFieldValue`
    // with the value to the first item in the dropdown list.

    // In other instances of `FilterableSelect`, we want to call the `onChange` callback if the user has cleared the input field.
    // However, in this case, since we always require an employee, we don't want to call the `onChange` callback if the user has cleared the input field.
    if (event.target.value == null || event.selectionConfirmed === false) return;
    return updateFieldValue('employeeId', event.target.value);
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const invalidStartDate = validateStartDate(company, startDate);

  const employee = emloyeesData?.employees;
  const employeeSelectOptions = (() => {
    const options = employee && employee.length > 0 ? employee : [{ employeeId: userData?.employeeId ?? '', name: userData?.name ?? '' }];
    return options.map((c) => ({ text: c.name, value: c.employeeId }));
  })();

  return (
    <Dialog
      disableAutoFocus
      disableEscKey={false}
      enableBackgroundUI={false}
      onCancel={() => onClose()}
      open
      showCloseIcon
      title={I18njs.t(title)}
      data-cy="create-timesheet-modal"
    >
      <Form
        saveButton={
          <Button
            data-pendo-id="create-timesheet-confirm"
            buttonType={{
              default: 'primary',
              embedded: 'secondary',
            }}
            onClick={handleSubmit}
            disabled={!!invalidStartDate}
            ml={1}
            data-cy="create-timesheet-create-timesheet-button"
          >
            <Translate scope={submitButtonText} />
          </Button>
        }
        leftSideButtons={
          <Button
            data-pendo-id="create-timesheet-cancel"
            buttonType={{
              default: 'tertiary',
              embedded: 'secondary',
            }}
            onClick={onClose}
            data-cy="create-timesheet-cancel-button"
          >
            <Translate scope="timesheets.Cancel" />
          </Button>
        }
        fieldSpacing={0}
      >
        <InputContent>
          <DateInputContent>
            <FieldBox data-cy="create-timesheet-begin-date-label">
              <DateInput
                allowEmptyValue
                error={invalidStartDate || undefined}
                label={I18njs.t('timesheets.BeginDate')}
                name="start-date-input"
                value={startDate.format(TIME_FORMAT)}
                onChange={onStartDateFieldChange}
                mb={0}
              />
              <FieldHelp scope="timesheets.BeginDateHelp" />
            </FieldBox>
            <FieldBox data-cy="create-timesheet-end-date-label">
              <DateInput
                readOnly
                label={I18njs.t('timesheets.EndDate')}
                name="end-date-input"
                onChange={() => {}}
                value={endDate.format(TIME_FORMAT)}
                mb={0}
              />
              <FieldHelp scope="timesheets.EndDateHelp" />
            </FieldBox>
          </DateInputContent>
          {staff && (
            <SelectContent>
              <SelectLabel scope="timesheets.Employee" />
              <FilterableSelect
                placeholder={I18njs.t('timesheet.SearchPromptText')}
                onChange={handleEmployeeChange}
                value={employeeId ?? ''}
              >
                {employeeSelectOptions.map((option) => (
                  <Option key={option.value} value={option.value} text={option.text ?? ''} />
                ))}
              </FilterableSelect>
            </SelectContent>
          )}
          <DescriptionField data-cy="create-timesheet-description-label">
            <Textarea
              autoFocus
              label={I18njs.t('timesheets.Description')}
              name="description"
              onChange={updateTextField('description')}
              value={description}
            />
          </DescriptionField>
          {showIncludeEntriesBox && (
            <Box data-cy="create-timesheet-include-entries-label">
              <Checkbox
                label={I18njs.t('timesheets.IncludeEntries')}
                name="include-entries"
                onChange={() => {
                  updateFieldValue('includeEntries', !includeEntries);
                }}
                checked={includeEntries}
              />
            </Box>
          )}
        </InputContent>
      </Form>
    </Dialog>
  );
};
